import '@/Monitoring';
import 'simplebar-react/dist/simplebar.min.css';
import '@/stores/init';

import { handleRequestError, IgnoreError } from '@/error-handler';
import * as route from '@/constants/route';
import useUserStore from '@/stores/useUserStore';
import anewSignIn from '@/utils/anewSignIn';
import loadUserProfile from '@/utils/loadUserProfile';
import { getAuthToken } from '@/utils/authToken';
import { useNotice } from '@/utils/notice';
import { css, injectGlobal } from '@emotion/css';
import { defineApp } from '@umijs/max';
import { App, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { PropsWithChildren } from 'react';
import { SWRConfig } from 'swr';
import { hasSubject } from '@/utils/subject';
import { useShallow } from 'zustand/shallow';
import useThemeStore from './stores/useThemeStore';
import getDomainPlatform, {
  DomainPlatformValue,
} from './utils/getDomainPlatform';
import getAppClient, { AppClientValue } from './utils/getAppClient';
import downSvg from './images/down.svg';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
injectGlobal`
  #root {
    height: 100%;
  }
  
  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 0s 600000s, color 0s 600000s !important;
  }
  
  .rc-virtual-list-scrollbar.rc-virtual-list-scrollbar-vertical {
    width: 6px !important;
  }
  .rc-virtual-list-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1)!important;
    border-radius: 4px !important;
  }

  .ant-cascader-menu {
    ::-webkit-scrollbar {
      width: 6px;
    }
    ::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
  }

  .ant-select .ant-select-arrow {
    width: 12px;
    height: 12px;
    background: url(${downSvg}) no-repeat center / contain;
  }

  .ant-select .ant-select-arrow .anticon,
  .ant-select .ant-select-arrow svg {
    display: none;
  }

 .ant-modal-content {
    margin-bottom: 80px;
  }
`;

function Providers(props: PropsWithChildren) {
  const { children } = props;

  const [
    antdThemeColor,
    antdPrimaryColor,
    antdPrimaryHoverColor,
    filterBorderColor,
    filterHoverBodrderColor,
    filterActiveBorderColor,
    antdDefaultHoverBoderColor,
    antdDefaultHoverTextColor,
    antdDefaultHoverBgColor,
  ] = useThemeStore(
    useShallow((s) => [
      s.antdThemeColor,
      s.antdPrimaryColor,
      s.antdPrimaryHoverColor,
      s.filterBorderColor,
      s.filterHoverBorderColor,
      s.filterActiveBorderColor,
      s.antdDefaultHoverBoderColor,
      s.antdDefaultHoverTextColor,
      s.antdDefaultHoverBgColor,
    ]),
  );

  return (
    <SWRConfig
      value={{
        shouldRetryOnError: false,
        revalidateOnFocus:
          process.env.UMI_APP_SWR_REVALIDATE_ON_FOCUS !== 'false',
        onError: (error) => {
          if (error.isAxiosError) {
            handleRequestError(error);
            throw new IgnoreError();
          }

          throw error;
        },
      }}
    >
      <ConfigProvider
        locale={zhCN}
        theme={{
          token: {
            colorPrimary: antdThemeColor || '#1677FF',
            colorBgSpotlight: 'rgba(47, 50, 53, 1)',
          },
          components: {
            Table: {
              headerSplitColor: 'transparent',
            },
            Modal: {
              borderRadiusLG: 12,
              titleFontSize: 16,
              titleLineHeight: '22px',
              titleColor: 'rgba(37, 40, 43, 1)',
            },
            Input: {
              fontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              colorBorder: filterBorderColor,
              hoverBorderColor: filterHoverBodrderColor,
              activeBorderColor: filterActiveBorderColor,
            },
            Select: {
              fontSize: 13,
              optionFontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              optionSelectedFontWeight: 400,
              colorBorder: filterBorderColor,
              hoverBorderColor: filterHoverBodrderColor,
              activeBorderColor: filterActiveBorderColor,
            },
            Button: {
              fontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              colorBorder: filterBorderColor,
              colorPrimaryHover: filterHoverBodrderColor,
              colorPrimaryActive: filterActiveBorderColor,
            },
            Cascader: {
              fontSize: 13,
              optionSelectedFontWeight: 400,
            },
          },
        }}
        modal={{
          className: css`
            .ant-modal-footer,
            .ant-modal-confirm-btns {
              .ant-btn {
                background: ${antdPrimaryColor} !important;
                color: rgba(255, 255, 255, 1);
                &:hover {
                  background: ${antdPrimaryHoverColor}!important;
                }
              }
              .ant-btn-primary {
                background: ${antdPrimaryColor} !important;
                background: ${antdPrimaryColor} !important;
                color: rgba(255, 255, 255, 1);
                &:disabled {
                  background: ${antdPrimaryColor} !important;
                  opacity: 0.1;
                }
                &:hover {
                  background: ${antdPrimaryHoverColor} !important;
                }
              }
              .ant-btn-default {
                background: #ffffff !important;
                color: rgba(37, 40, 43, 1);
                &:hover {
                  border: 1px solid ${antdDefaultHoverBoderColor} !important;
                  background: ${antdDefaultHoverBgColor} !important;
                  color: ${antdDefaultHoverTextColor} !important;
                }
              }
            }
          `,
        }}
      >
        {children}
      </ConfigProvider>
    </SWRConfig>
  );
}

function AppExtends() {
  useNotice();
  return null;
}

export default defineApp({
  rootContainer: (container) => (
    <Providers>
      <App>
        <AppExtends />
        {container}
      </App>
    </Providers>
  ),
  onRouteChange: () => {
    const { pathname } = window.location;
    const isLoginRoute =
      pathname === '/login' || pathname.startsWith(`/${route.loginPrefix}/`);
    const isStudentRoute = pathname.startsWith(route.studentPrefix);
    const isStudentClient = getAppClient() === AppClientValue.Student;

    if (
      getDomainPlatform() === DomainPlatformValue.Ipe &&
      [
        route.classroomCourseware,
        route.intelligenceCourseware,
        route.intelligenceResource,
      ].some((v) => window.location.pathname.includes(v))
    ) {
      window.location.href = '/not-found';
    }

    if (
      getDomainPlatform() === DomainPlatformValue.Fs &&
      ['/agent-material'].some((v) => window.location.pathname.includes(v))
    ) {
      window.location.href = '/not-found';
    }

    if (
      getDomainPlatform() !== DomainPlatformValue.Fs &&
      [route.fsAgentMaterial].some((v) => window.location.pathname.includes(v))
    ) {
      window.location.href = '/not-found';
    }

    if (!isLoginRoute && !isStudentRoute && !isStudentClient) {
      if (!hasSubject()) {
        anewSignIn('没有选择学科，请重新登录');
        return;
      }
    }

    if (
      !isStudentRoute &&
      !isStudentClient &&
      getAuthToken() &&
      !useUserStore.getState().isFilled()
    ) {
      loadUserProfile();
    }
  },
  render: async (oldRender) => {
    if (
      !window.location.pathname.startsWith(route.studentPrefix) &&
      getAppClient() !== AppClientValue.Student &&
      getAuthToken()
    ) {
      await loadUserProfile();
    }

    oldRender();
  },
});
